import { Component, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import {
  NbButtonModule,
  NbCardModule,
  NbIconModule,
  NbOptionModule,
  NbSelectModule,
  NbSpinnerModule,
} from '@nebular/theme';
import { TranslateModule } from '@ngx-translate/core';
import { BehaviorSubject } from 'rxjs';
import { KPI } from '../../../../../@core/api';
import { ScopeService } from '../../../../../@core/utils';
import { idMapToArray, toLoadingState } from '../../../../../@core/utils/scope.service';

@Component({
  selector: 'app-category-selector',
  template: `
    <nb-card style="width: 45vw; height: 100%;">
      <nb-card-body class="investment-body">
        <form [formGroup]="categoryForm">
          <div class="form-content">
            <!-- Super Category -->
            <div class="form-field" [nbSpinner]="(superCategories$ | async)?.loading">
              <label for="super_category">{{ 'Super category' | translate }}</label>
              <nb-select
                id="super_category"
                formControlName="super_category"
                fullWidth
                placeholder="{{ 'Select a super category' | translate }}"
              >
                <nb-option [value]="null">-</nb-option>
                @for (superCat of (superCategories$ | async)?.data; track superCat.id) {
                  <nb-option [value]="superCat">
                    {{ superCat.name }}
                  </nb-option>
                }
              </nb-select>
            </div>

            <!-- Category -->
            <div class="form-field" [nbSpinner]="(categories$ | async)?.loading">
              <label for="category">{{ 'Category' | translate }}</label>
              <nb-select
                id="category"
                formControlName="category"
                fullWidth
                placeholder="{{ 'Select a category' | translate }}"
                [disabled]="!categoryForm.get('super_category')?.value"
              >
                <nb-option [value]="null">-</nb-option>
                @for (cat of filteredCategories$ | async; track cat.id) {
                  <nb-option [value]="cat">
                    {{ cat.name }}
                  </nb-option>
                }
              </nb-select>
            </div>

            <!-- Sub Category -->
            <div class="form-field">
              <label for="sub_category">{{ 'Sub category' | translate }}</label>
              <nb-select
                id="sub_category"
                formControlName="sub_category"
                fullWidth
                placeholder="{{ 'Select a sub category' | translate }}"
                [disabled]="!categoryForm.get('category')?.value"
              >
                <nb-option [value]="null">-</nb-option>
                @for (subCat of filteredSubCategories$ | async; track subCat.id) {
                  <nb-option [value]="subCat">
                    {{ subCat.name }}
                  </nb-option>
                }
              </nb-select>
            </div>
          </div>
        </form>
      </nb-card-body>
      @if (hasChanges()) {
        <nb-card-footer class="investment-footer">
          <button nbButton class="investment-next" (click)="onSave()">
            {{ 'Save' | translate }}
          </button>
        </nb-card-footer>
      }
    </nb-card>
  `,
  styles: [`
    :host {
      display: block;
      height: 100%;
    }

    :host ::ng-deep nb-card {
      border-radius: 0;
      box-shadow: none;
      display: flex;
      flex-direction: column;
      height: 100%;
    }

    .investment-header {
      font-size: 1rem;
      background-color: transparent;
      padding: 1rem 1.5rem;
      border-bottom: 1px solid #e2e8f0;
      display: flex;
      justify-content: space-between;
      align-items: center;
    }

    .header-title {
      margin: 0;
      font-size: 1rem;
      font-weight: 500;
      color: #c6353f;
      flex: 1;
      text-align: center;
    }

    .close-button {
      color: white;
      padding: 0;
      margin: 0;
      border: none;
      background: transparent;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      width: 2.5rem;
      height: 2.5rem;
    }

    :host ::ng-deep .close-button nb-icon {
      font-size: 1.5rem;
      color: white;
    }

    :host ::ng-deep .close-button:hover {
      background-color: rgba(255, 255, 255, 0.1);
      border-radius: 4px;
    }

    .investment-body {
      padding: 0;
      flex: 1;
      overflow-y: auto;
      background-color: #fff;
    }

    .form-content {
      display: flex;
      flex-direction: column;
      gap: 0;
    }

    .form-field {
      padding: 7px 10px;
      border-bottom: 1px solid #e2e8f0;
    }

    .form-field label {
      display: block;
      margin-bottom: 0.5rem;
      font-weight: 500;
      color: #00729a;
      font-size: 0.9rem;
    }

    .investment-footer {
      display: flex;
      justify-content: center;
      padding: 1rem 1.5rem;
      box-shadow: 0 0 2.5px 0 #c6c6c6;
      background-color: #c6e1fc;
      flex-shrink: 0;
      position: sticky;
      bottom: 0;
      z-index: 10;
    }

    :host ::ng-deep .investment-footer button {
      background: transparent;
      border-color: #333333 !important;
      color: #333333 !important;
      font-size: 13px !important;
      width: 70%;
      text-transform: uppercase;
    }

    :host ::ng-deep .investment-footer button.investment-next {
      background-color: #eef1f7 !important;
    }

    :host ::ng-deep .investment-footer button:hover:not(:disabled) {
      border-color: #fff !important;
      background: #fff !important;
      transition: 0.1s ease-in-out;
    }

    :host ::ng-deep .investment-footer button:active {
      background-color: #244d75;
      color: white !important;
    }

    :host ::ng-deep .investment-footer button:disabled {
      border-color: #9c9c9c !important;
      color: #9c9c9c !important;
      background: #ccc !important;
    }

    :host ::ng-deep nb-select {
      width: 100%;
    }

    :host ::ng-deep .form-field nb-select button {
      width: 100%;
      text-align: left;
    }
  `],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    NbCardModule,
    NbSelectModule,
    NbOptionModule,
    NbButtonModule,
    NbIconModule,
    NbSpinnerModule,
    TranslateModule,
  ],
})
export class CategorySelectorComponent implements OnInit {
  @Input() investment!: KPI.MappedInvestment;
  @Input() readOnly: boolean = false;
  @Output() save = new EventEmitter<{
    super_category?: any;
    category?: any;
    sub_category?: any;
  }>();

  private scopeService = inject(ScopeService);

  public categoryForm = new FormGroup({
    super_category: new FormControl<any>(null),
    category: new FormControl<any>(null),
    sub_category: new FormControl<any>(null),
  });

  public superCategories$ = this.scopeService.superCategories$.pipe(
    idMapToArray(),
    toLoadingState<KPI.SuperCategory[]>(),
  );

  public categories$ = this.scopeService.categories$.pipe(
    idMapToArray(),
    toLoadingState<KPI.Category[]>(),
  );

  public subCategories$ = this.scopeService.subCategories$.pipe(
    idMapToArray(),
    toLoadingState<KPI.SubCategory[]>(),
  );

  // Filtered categories based on selected super category
  public filteredCategories$ = new BehaviorSubject<KPI.Category[]>([]);

  // Filtered sub-categories based on selected category
  public filteredSubCategories$ = new BehaviorSubject<KPI.SubCategory[]>([]);

  private initialValues: any;

  ngOnInit() {
    // Load initial values from investment
    this.categories$.subscribe(categoriesState => {
      if (categoriesState.data) {
        // Initially show all categories
        this.filteredCategories$.next(categoriesState.data);

        // Find and set selected category if exists
        if (this.investment.category_object) {
          const category = categoriesState.data.find(
            cat => cat.id === this.investment.category_object?.id
          );
          if (category) {
            this.categoryForm.patchValue({ category }, { emitEvent: false });
          }
        }
      }
    });

    this.superCategories$.subscribe(superCategoriesState => {
      if (superCategoriesState.data && this.investment.super_category_object) {
        const superCategory = superCategoriesState.data.find(
          sc => sc.id === this.investment.super_category_object?.id
        );
        if (superCategory) {
          this.categoryForm.patchValue({ super_category: superCategory }, { emitEvent: false });
        }
      }
    });

    this.subCategories$.subscribe(subCategoriesState => {
      if (subCategoriesState.data) {
        // Find and set selected sub-category if exists
        if (this.investment.sub_category_object) {
          const subCategory = subCategoriesState.data.find(
            sc => sc.id === this.investment.sub_category_object?.id
          );
          if (subCategory) {
            this.categoryForm.patchValue({ sub_category: subCategory }, { emitEvent: false });
            // Also update filtered list based on current category
            if (this.investment.category) {
              const filtered = subCategoriesState.data.filter(
                sc => sc.parent === this.investment.category
              );
              this.filteredSubCategories$.next(filtered);
            }
          }
        }
      }
    });

    // Store initial values for change detection
    this.initialValues = {
      super_category: this.investment.super_category_object,
      category: this.investment.category_object,
      sub_category: this.investment.sub_category_object,
    };

    if (this.readOnly) {
      this.categoryForm.disable();
    }

    // Listen to super category changes to filter categories
    this.categoryForm.get('super_category')?.valueChanges.subscribe(superCategory => {
      this.categoryForm.patchValue(
        { category: null, sub_category: null },
        { emitEvent: false }
      );
      this.filteredSubCategories$.next([]);

      if (superCategory) {
        // Filter categories that belong to this super category
        this.categories$.subscribe(categoriesState => {
          if (categoriesState.data) {
            const filtered = superCategory.children
              ? categoriesState.data.filter(cat => superCategory.children.some((child: any) => child.id === cat.id))
              : categoriesState.data.filter(cat => cat.parent === superCategory.id);
            this.filteredCategories$.next(filtered);
          }
        });
      } else {
        // Show all categories if no super category selected
        this.categories$.subscribe(categoriesState => {
          if (categoriesState.data) {
            this.filteredCategories$.next(categoriesState.data);
          }
        });
      }
    });

    // Listen to category changes to filter sub-categories
    this.categoryForm.get('category')?.valueChanges.subscribe(category => {
      if (category) {
        // Clear sub-category when category changes
        this.categoryForm.patchValue({ sub_category: null }, { emitEvent: false });

        // Update filtered sub-categories
        this.subCategories$.subscribe(subCategoriesState => {
          if (subCategoriesState.data) {
            const filtered = subCategoriesState.data.filter(
              sc => sc.parent === category.id
            );
            this.filteredSubCategories$.next(filtered);
          }
        });
      } else {
        this.filteredSubCategories$.next([]);
      }
    });
  }

  hasChanges(): boolean {
    const currentValues = this.categoryForm.value;
    return (
      currentValues.super_category?.id !== this.initialValues.super_category?.id ||
      currentValues.category?.id !== this.initialValues.category?.id ||
      currentValues.sub_category?.id !== this.initialValues.sub_category?.id
    );
  }

  onClose() {
    this.save.emit(null);
  }

  onSave() {
    const formValue = this.categoryForm.value;

    this.save.emit({
      super_category: formValue.super_category || null,
      category: formValue.category || null,
      sub_category: formValue.sub_category || null,
    });
  }
}
